/**
 * @file: index.ts
 * @author: eric <eric.blueplus@gmail.com>
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 */

import { StatusColors } from '@euler/components/Colors';
import { FontFamily } from '@euler/components/typography';
import {
  getDefectiveLevel,
  getDefectiveLevelColor,
  getDefectiveLevelTextShort,
} from '@euler/functions/getDefectiveLevel';
import {
  AbnormalLevel,
  InspectionTaskEventSubType,
  InspectionTaskEventType,
  SeverityLevel,
} from '@euler/model/enum';
import { DefaultTheme } from 'styled-components';
import { registerEventType } from '../../registry';

const register = registerEventType(InspectionTaskEventType.Inspection);

function itemInspected() {
  register(InspectionTaskEventSubType.ItemInspected)((item, data) => {
    const abnormalLevel = data.a as AbnormalLevel;
    const severityLevel = data.s as SeverityLevel;
    const level = getDefectiveLevel(abnormalLevel, severityLevel);
    const defectiveLabel = getDefectiveLevelTextShort(level);
    const color = (theme: DefaultTheme) => getDefectiveLevelColor(theme, level);
    return item
      .label([`${data.name}: `, { text: data.label || defectiveLabel, color }])
      .medias('m');
  });
}

function customIssueAdded() {
  register(InspectionTaskEventSubType.CustomIssueAdded)((item, data) => {
    const name =
      (data.itemName === '自定义' ? null : data.itemName) ?? data.siteName;
    const abnormalLevel = data.a as AbnormalLevel;
    const severityLevel = data.s as SeverityLevel;
    const level = getDefectiveLevel(abnormalLevel, severityLevel);
    const defectiveLabel = getDefectiveLevelTextShort(level);
    const color = (theme: DefaultTheme) => getDefectiveLevelColor(theme, level);
    return item
      .label([`${name}: `, { text: data.label || defectiveLabel, color }])
      .medias('m');
  });
}

function finished() {
  register(InspectionTaskEventSubType.InspectionFinished)((item, data) => {
    item.label(data?.[0] ? `${data?.[0]}已完成` : '常规检测已完成');
  });
}

function reportGenerated() {
  register(InspectionTaskEventSubType.InspectionReportGenerated)(item => {
    item.label('推送检测报告');
  });
}

function reportUpdated() {
  register(InspectionTaskEventSubType.InspectionReportUpdated)(item => {
    item.label('检测报告已更新');
  });
}

function inspectionFlowAdded() {
  register(InspectionTaskEventSubType.InspectionFlowAdded)((item, data) => {
    item
      .label({
        text: `添加检测任务: ${data?.name}`,
        color: '#998900',
        fontFamily: FontFamily.NotoSans.Medium,
      })
      .tagline({
        text: `派工给技师: ${data?.assignee?.name}`,
        color: '#555',
      });
  });
}

function inspectionFlowStart() {
  register(InspectionTaskEventSubType.InspectionFlowStarted)((item, data) => {
    item.label({
      text: `开始检测任务: ${data?.name}`,
      fontFamily: FontFamily.NotoSans.Medium,
      color: StatusColors.Info,
    });
  });
}

function inspectionFlowCanceled() {
  register(InspectionTaskEventSubType.InspectionFlowCanceled)((item, data) => {
    item.label({
      text: `取消检测任务: ${data?.name}`,
      fontFamily: FontFamily.NotoSans.Medium,
      color: StatusColors.Danger,
    });
  });
}

function inspectionFlowFinished() {
  register(InspectionTaskEventSubType.InspectionFlowFinished)((item, data) => {
    item.label({
      text: `完成检测任务: ${data?.name}`,
      fontFamily: FontFamily.NotoSans.Medium,
      color: StatusColors.Success,
    });
  });
}

itemInspected();
customIssueAdded();
finished();
reportGenerated();
reportUpdated();
inspectionFlowAdded();
inspectionFlowStart();
inspectionFlowCanceled();
inspectionFlowFinished();
